import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Box,
  Button,
  InputAdornment,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Autocomplete
} from '@mui/material';
import { Upload, Close } from '@mui/icons-material';
import { submitLoanApplication } from './api';
import apiService from '../Transactions/api'; // Use the same API service as CollectCash

const LoanForm = ({ onClose, selectedMemberId = null }) => {
  const [loanDocument, setLoanDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [loanForm, setLoanForm] = useState({
    member_id: selectedMemberId || '',
    selectedMember: null, // Store the full member object
    amount: 0,
    installments: '',
    guarantors: [],
    selectedGuarantors: [], // Store the full guarantor objects
    officer_name: '',
    notes: '',
    loan_type: 'longterm'
  });

  // Fetch all members when component mounts - same pattern as CollectCash
  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const data = await apiService.fetchMembers();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching members:", err);
      setErrorMessage("Failed to load members: " + err.message);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleFileUpload = (event) => {
    setLoanDocument(event.target.files[0]);
  };

  const handleFormChange = (field, value) => {
    setLoanForm({
      ...loanForm,
      [field]: value
    });
  };

  const handleLoanSubmit = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
  
      // Form validation
      if (!loanForm.member_id) {
        setErrorMessage('Please select a member');
        return;
      }
  
      if (loanForm.amount <= 0) {
        setErrorMessage('Please enter a valid amount');
        return;
      }
  
      if (!loanForm.officer_name) {
        setErrorMessage('Please enter officer name');
        return;
      }
  
      if (loanForm.guarantors.length === 0) {
        setErrorMessage('Please select at least one guarantor');
        return;
      }
  
      if (!loanForm.installments || loanForm.installments < 1) {
        setErrorMessage('Please enter valid number of installments');
        return;
      }
  
      const formData = new FormData();
    
      // Add all fields except guarantors and selectedMember/selectedGuarantors
      Object.keys(loanForm).forEach(key => {
        if (!['guarantors', 'selectedMember', 'selectedGuarantors'].includes(key)) {
          formData.append(key, loanForm[key]);
        }
      });
      
      // Add guarantors as JSON string
      formData.append('guarantors', JSON.stringify(loanForm.guarantors));
  
      // Add loan document with the correct field name that matches your model
      if (loanDocument) {
        formData.append('document_url', loanDocument);
      }
  
      // Log the formData contents for debugging
      console.log('Final FormData being sent:');
      for (const [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
  
      await submitLoanApplication(formData);
      onClose(true); // Close with success flag
    } catch (error) {
      console.error('Loan submission failed:', error);
      
      // Enhanced error handling
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.details) {
          // Handle validation errors from serializer
          const errorMessages = Object.entries(errorData.details)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          setErrorMessage(`Validation errors:\n${errorMessages}`);
        } else if (errorData.error) {
          setErrorMessage(errorData.error);
        } else {
          setErrorMessage('Failed to submit loan application');
        }
      } else {
        setErrorMessage('Failed to submit loan application');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get available guarantors (exclude selected member)
  const availableGuarantors = members.filter(
    member => member.id !== loanForm.member_id
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Autocomplete
          options={Array.isArray(members) ? members : []}
          getOptionLabel={(option) => `${option.name} (${option.member_id})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Member"
              required
              fullWidth
              error={loanForm.member_id === '' && errorMessage !== ''}
              helperText={loanForm.member_id === '' && errorMessage !== '' ? 'Member is required' : ''}
            />
          )}
          onChange={(event, newValue) => {
            handleFormChange('selectedMember', newValue);
            handleFormChange('member_id', newValue ? newValue.id : '');
          }}
          value={loanForm.selectedMember}
          loading={loadingMembers}
          loadingText="Loading members..."
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Loan Amount"
          type="number"
          required
          value={loanForm.amount}
          onChange={(e) => handleFormChange('amount', parseFloat(e.target.value) || 0)}
          InputProps={{
            startAdornment: <InputAdornment position="start">KES</InputAdornment>,
          }}
          error={loanForm.amount <= 0 && errorMessage !== ''}
          helperText={loanForm.amount <= 0 && errorMessage !== '' ? 'Valid amount is required' : ''}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Officer Name"
          required
          value={loanForm.officer_name}
          onChange={(e) => handleFormChange('officer_name', e.target.value)}
          error={loanForm.officer_name === '' && errorMessage !== ''}
          helperText={loanForm.officer_name === '' && errorMessage !== '' ? 'Officer name is required' : ''}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Number of Installments"
          type="number"
          required
          value={loanForm.installments}
          onChange={(e) => handleFormChange('installments', e.target.value === '' ? '' : parseInt(e.target.value))}
          error={(loanForm.installments === '' || loanForm.installments < 1) && errorMessage !== ''}
          helperText={(loanForm.installments === '' || loanForm.installments < 1) && errorMessage !== '' ? 'At least 1 installment is required' : ''}
        />
      </Grid>

      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={availableGuarantors}
          getOptionLabel={(option) => `${option.name} (${option.member_id})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Guarantors"
              required
              fullWidth
              error={loanForm.guarantors.length === 0 && errorMessage !== ''}
              helperText={loanForm.guarantors.length === 0 && errorMessage !== '' ? 'At least one guarantor is required' : ''}
            />
          )}
          onChange={(event, newValue) => {
            handleFormChange('selectedGuarantors', newValue);
            handleFormChange('guarantors', newValue.map(v => v.id));
          }}
          value={loanForm.selectedGuarantors}
          loading={loadingMembers}
          loadingText="Loading members..."
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ border: '1px dashed grey', borderRadius: 1, p: 2 }}>
          <input
            accept="application/pdf,image/*"
            style={{ display: 'none' }}
            id="loan-document-upload"
            type="file"
            onChange={handleFileUpload}
          />
          <label htmlFor="loan-document-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload />}
            >
              Upload Loan Document (Optional)
            </Button>
          </label>
          {loanDocument && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">
                {loanDocument.name}
              </Typography>
              <IconButton size="small" onClick={() => setLoanDocument(null)}>
                <Close />
              </IconButton>
            </Box>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Additional Notes"
          multiline
          rows={3}
          value={loanForm.notes}
          onChange={(e) => handleFormChange('notes', e.target.value)}
        />
      </Grid>

      {errorMessage && (
        <Grid item xs={12}>
          <Alert severity="error">
            {errorMessage}
          </Alert>
        </Grid>
      )}

      {/* Submit Button */}
      <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleLoanSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Processing...' : 'Submit Loan Application'}
        </Button>
      </Grid>
    </Grid>
  );
};

export default LoanForm;